import InvoicePeriodisationReview from '../../Accounting/Periodisering/InvoicePeriodisationReview';
import React, { useReducer, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Link, useParams, useHistory, useLocation } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils.js';
import { formatISO, parseISO, addDays } from 'date-fns';
import swal from 'sweetalert';
import { toast } from 'sonner';
import PageHeader from '../../../layouts/PageHeader';
import { Receipt } from 'lucide-react';
import NewArticleModal from '../Factoring/Articles/NewArticleModal.js';
import NewCustomerModal from '../Factoring/Customers/NewCustomerModal.js';

// Enhanced Components
import { Form } from 'react-bootstrap';
import InvoiceFormBootstrap from './newinvoiceform/InvoiceFormBootstrap.jsx';
import CustomerCompactCard from './newinvoiceform/CustomerCompactCard.jsx';
import InvoiceStickyBar from './newinvoiceform/InvoiceStickyBar.jsx';
import { calculateInvoiceTotals } from './newinvoiceform/invoiceTotals';

import { useInvoiceSettings } from '../../../../context/InvoiceSettingsContext';
import { useAccountingSettings } from '../../../../context/AccountingSettingsContext';
import { useChangeTracking } from './hooks/useChangeTracking';
import { invoiceReducer, createInitialState } from './reducers/invoiceReducers.jsx';
import { validationSchema } from './utils/invoiceValidation.jsx';
import { useSelector } from 'react-redux';
import { getSelectedVatStatus } from '../../../../store/selectors/AuthSelectors';
import {
  resolveTreatment as resolveVatTreatment,
  explainTreatment as explainVatTreatment,
  describeTreatment as describeVatTreatment,
  VAT_TREATMENTS,
  VAT_TREATMENT_LABELS
} from '../../../../utils/vatTreatment';

const transformApiItemsToInternal = (apiItems = []) => {
  return apiItems.map((item, index) => ({
    id: item.item_id || `item-${index}`, // API uses 'item_id'
    productcode: item.productcode || '', // This field exists in API
    description: item.description || '', // This field exists in API
    unitprice: parseFloat(item.unitprice || 0), // API uses 'unitprice' as string
    quantity: parseFloat(item.quantity || 1),
    unit: item.unit || 'st', // API has 'unit' field
    tax_rate: item.tax_rate || '25%', // API uses 'tax_rate'
    discount: parseFloat(item.discount || 0),
    type: item.type || 'Product', // API has 'type' field
    item_id: item.item_id, // Keep original API ID for updates
    article_id: item.article_id, // Keep article reference
    isNew: false // Mark as existing item
  }));
};

// Helper function to normalize data for comparison
const normalizeDataForComparison = (state) => {
  return {
    invoiceData: {
      customerid: state.customerid,
      customer: state.customer ? {
        id: state.customer.id,
        name: state.customer.name,
        email: state.customer.email
      } : null,
      invoiceNumber: state.invoiceNumber,
      invoiceDate: state.invoiceDate?.getTime(),
      dueDate: state.dueDate?.getTime(),
      currency: state.currency,
      roundTotal: state.roundTotal,
      notes: state.notes,
      status: state.status,
      selectedTemplate: state.selectedTemplate
    },
    items: state.items.map(item => ({
      id: item.id,
      productcode: item.productcode || '',
      description: item.description || '',
      unitprice: Number(item.unitprice) || 0,
      quantity: Number(item.quantity) || 1,
      unit: item.unit || 'st',
      tax_rate: item.tax_rate || '25%',
      discount: Number(item.discount) || 0,
      type: item.type || 'Product'
    })),
    deliveryOptions: state.deliveryOptions,
    paymentMethods: state.paymentMethods,
    invoiceAddressOverride: state.invoiceAddressOverride,
    invoiceContactOverride: state.invoiceContactOverride
  };
};

/**
 * The moms-conflict payload from a refused publish, or null for any other error.
 * A 'download' publish sets responseType 'blob', so an error body arrives as a
 * Blob and has to be read rather than taken off `.data`.
 */
async function readVatConflict(error) {
  const data = error?.response?.data;
  if (!data) return null;
  try {
    const parsed = data instanceof Blob ? JSON.parse(await data.text()) : data;
    if (parsed?.code !== 'VAT_TREATMENT_CONFLICT') return null;
    return parsed.conflict || { message: parsed.message };
  } catch (e) {
    return null;
  }
}

const InvoiceForm = () => {
  const { companyId, invoiceId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const isEditMode = Boolean(invoiceId);

  // Get pre-selected customer ID from query params (e.g., when coming from customer details page)
  const preSelectedCustomerId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('customerId');
  }, [location.search]);

  // Refs and contexts
  const invoiceRef = useRef(null);
  const skipNavigationWarningRef = useRef(false);
  const { invoiceSettings, isLoading: invoiceSettingsLoading, refreshInvoiceSettings } = useInvoiceSettings();
  const { accountingSettings } = useAccountingSettings();
  // tenant.vat_status drives the first precedence step in resolveTreatment.
  // Plumbed through Session B's selectedCompany Redux propagation.
  const tenantVatStatus = useSelector(getSelectedVatStatus);

  // Initialize state with reducer
  const [state, dispatch] = useReducer(
    invoiceReducer,
    { invoiceId, isEditMode, invoiceSettings },
    ({ invoiceId, isEditMode, invoiceSettings }) => createInitialState(invoiceId, isEditMode, invoiceSettings)
  );

  // Additional state
  const [articles, setArticles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewArticleModal, setShowNewArticleModal] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Payment methods now come from company settings. Reconcile after settings
  // arrive and after loading a draft, which may have an empty/old selection.
  useEffect(() => {
    if (invoiceSettingsLoading || !invoiceSettings?.paymentMethods) return;
    const methods = ['bankgiro', 'plusgiro', 'paypal', 'swish'];
    const configured = Object.fromEntries(methods.map(method => [method, invoiceSettings.paymentMethods[method] === true]));
    if (methods.some(method => Boolean(state.paymentMethods?.[method]) !== configured[method])) {
      dispatch({ type: 'UPDATE_INVOICE_DATA', payload: { paymentMethods: configured } });
    }
  }, [invoiceSettings, invoiceSettingsLoading, state.paymentMethods]);

  // Settings open in another tab; returning to the editor should unblock it
  // without requiring a reload that could discard invoice edits.
  useEffect(() => {
    const refresh = () => refreshInvoiceSettings?.();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refreshInvoiceSettings]);

  // Change tracking - only start after data is loaded
  const trackedData = useMemo(() => {
    if (!isDataLoaded) return null;
    return normalizeDataForComparison(state);
  }, [state, isDataLoaded]);

  const { hasChanges, resetTracking, getChangedFields } = useChangeTracking(trackedData);

  // Validation
  const validationErrors = useMemo(() => {
    const errors = {};
    Object.keys(validationSchema).forEach(field => {
      const error = validationSchema[field](state[field], state);
      if (error) errors[field] = error;
    });
    return errors;
  }, [state]);

  const isFormValid = useMemo(() => {
    return Object.keys(validationErrors).length === 0 && state.customerid && state.items.length > 0;
  }, [validationErrors, state.customerid, state.items]);

  // The one sentence next to the Publicera button. Order = the order the
  // user fills the page in, so the first thing they read is the next thing
  // to do.
  const publishBlockedReason = useMemo(() => {
    if (invoiceSettingsLoading) return 'Hämtar fakturainställningar…';
    if (!state.customerid) return 'Välj en kund för att kunna publicera';
    if (validationErrors.invoiceDate) return validationErrors.invoiceDate;
    if (validationErrors.dueDate) return validationErrors.dueDate;
    if (validationErrors.currency) return validationErrors.currency;
    if (!state.items || state.items.length === 0) return 'Lägg till minst en rad';
    if (validationErrors.items) return validationErrors.items;
    if (!Object.values(state.paymentMethods || {}).some(Boolean)) return 'Aktivera minst en betalningsmetod i inställningarna';
    if (validationErrors.notes) return validationErrors.notes;
    return null;
  }, [state.customerid, state.items, state.paymentMethods, validationErrors, invoiceSettingsLoading]);

  // Payment methods are not edited on the invoice: they come from settings
  // and are the same on every invoice. The page only speaks up when settings
  // would block publishing.
  const paymentProblem = useMemo(() => {
    const configured = invoiceSettings?.paymentMethods || {};
    const enabled = Object.keys(state.paymentMethods || {}).filter((m) => state.paymentMethods[m]);
    if (enabled.length === 0) return 'Ingen betalningsmetod är aktiverad i inställningarna.';
    const missing = enabled
      .map((m) => (m === 'bankgiro' && !configured.bankgiroNumber ? 'Bankgironummer saknas i inställningarna'
        : m === 'plusgiro' && !configured.plusgiroNumber ? 'Plusgironummer saknas i inställningarna'
          : null))
      .filter(Boolean);
    return missing.length ? `${missing.join('. ')}.` : null;
  }, [state.paymentMethods, invoiceSettings]);

  const isTaxableInvoice = (state.vat_treatment || 'taxable') === 'taxable';
  const invoiceTotals = useMemo(
    () => calculateInvoiceTotals(state.items, { roundTotal: state.roundTotal, isTaxable: isTaxableInvoice }),
    [state.items, state.roundTotal, isTaxableInvoice]
  );

  // Data fetching methods
  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        getApiUrl(`${companyId}/invoices/${invoiceId}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        const { data: invoice } = response.data;

        console.log('Raw API invoice response:', invoice);
        console.log('Delivery fields in API response:', {
          delivery_method: invoice.delivery_method,
          delivery_email: invoice.delivery_email,
          delivery_phone: invoice.delivery_phone,
          delivery_subject: invoice.delivery_subject,
          delivery_message: invoice.delivery_message,
          payment_methods: invoice.payment_methods,
          payment_methods_type: typeof invoice.payment_methods,
          payment_methods_parsed: invoice.payment_methods ? (typeof invoice.payment_methods === 'string' ? 'will_parse' : 'already_parsed') : 'empty',
          selected_template: invoice.selected_template,
          notes: invoice.notes
        });

        // Parse payment methods from stored data
        let paymentMethods = {};
        if (invoice.payment_methods) {
          try {
            let methods = invoice.payment_methods;

            // If it's a string, parse it as JSON
            if (typeof methods === 'string') {
              methods = JSON.parse(methods);
            }

            // Convert to object format for the UI
            if (Array.isArray(methods)) {
              // Convert array to object format
              methods.forEach(method => {
                paymentMethods[method] = true;
              });
              console.log('Converted payment methods array to object:', paymentMethods);
            } else if (typeof methods === 'object' && methods !== null) {
              paymentMethods = methods;
              console.log('Using payment methods object directly:', paymentMethods);
            }
          } catch (e) {
            console.warn('Failed to parse payment methods:', e, 'Raw value:', invoice.payment_methods);
            paymentMethods = {};
          }
        }

        // Parse delivery options from stored fields
        const deliveryOptions = {
          method: invoice.delivery_method || 'download',
          email: invoice.delivery_email || '',
          phone: invoice.delivery_phone || '',
          message: invoice.delivery_message || '',
          subject: invoice.delivery_subject || 'Faktura'
        };

        console.log('Final parsed data for frontend:', {
          deliveryOptions,
          paymentMethods,
          selectedTemplate: invoice.selected_template
        });

        // Get default payment days from invoice settings
        const defaultPaymentDays = invoiceSettings?.defaultTerms?.paymentDays || 30;

        // First, create the basic transformed invoice WITH stored delivery options
        const transformedInvoice = {
          invoiceId: invoice.invoiceid,
          customerid: invoice.customerid,
          invoiceNumber: invoice.invoicenumber || '',
          invoiceDate: invoice.invoicedate ? parseISO(invoice.invoicedate) : new Date(),
          dueDate: invoice.deadline ? parseISO(invoice.deadline) : addDays(new Date(), defaultPaymentDays),
          currency: invoice.currency || 'SEK',
          roundTotal: Boolean(invoice.rounding),
          status: invoice.status || 'Draft',
          notes: invoice.notes || '',
          selectedTemplate: invoice.selected_template || 'classic',
          items: transformApiItemsToInternal(invoice.items || []),
          deliveryOptions: deliveryOptions,
          paymentMethods: paymentMethods,
          invoiceAddressOverride: invoice.address_override
            ? (typeof invoice.address_override === 'string' ? JSON.parse(invoice.address_override) : invoice.address_override)
            : null,
          invoiceContactOverride: invoice.contact_override
            ? (typeof invoice.contact_override === 'string' ? JSON.parse(invoice.contact_override) : invoice.contact_override)
            : null,
          use_accrual_method: Boolean(invoice.use_accrual_method),
          // VAT treatment (Session C). REQUIRED — the LOAD_INVOICE reducer
          // case reads `loadedInvoice.vat_treatment` to hydrate state. Without
          // this line the form falls back to 'taxable' when editing a
          // non-taxable invoice, and saving silently corrupts the stored
          // value (Tier 1 R-1).
          vat_treatment: invoice.vat_treatment || 'taxable'
        };

        // Load the basic invoice data first
        console.log('Dispatching LOAD_INVOICE with payment methods:', transformedInvoice.paymentMethods);
        dispatch({ type: 'LOAD_INVOICE', payload: transformedInvoice, invoiceSettings });

        // Handle customer separately
        if (transformedInvoice.customerid) {
          const customerFromInvoice = {
            id: invoice.customerid,
            name: invoice.customername,
            email: invoice.email,
            phone: invoice.phone,
            customer_number: invoice.kundnummer,
            type: invoice.orgnumber ? 'company' : 'person',
            orgnumber: invoice.orgnumber,
            addresses: [{
              address_type: 'billing',
              street1: invoice.street1,
              city: invoice.city,
              postal_code: invoice.postalcode,
              country: invoice.country || 'Sverige'
            }]
          };

          try {
            const fullCustomer = await fetchCustomerDetails(transformedInvoice.customerid);
            const customerToUse = fullCustomer || customerFromInvoice;

            // Set customer first — preserve saved due date when loading existing invoice
            dispatch({ type: 'SET_CUSTOMER', payload: customerToUse, preserveDueDate: true });

            // Heal a draft written before the treatment fix.
            //
            // Such a draft holds `taxable` with 25 % lines while its customer is
            // plainly an EU business, and nothing in the editor said so — the
            // publish guard refused, and "Gå tillbaka och rätta" returned to a
            // form that looked correct. A dead end for exactly the invoices that
            // needed fixing.
            //
            // Both directions (C1, closing plan):
            //   · taxable → zero-rated is healed in place — adopting a treatment
            //     that charges LESS is safe, and a deliberate override made after
            //     this runs still wins;
            //   · zero-rated → taxable is the revenue-losing direction and used
            //     to be silent. It is NOT healed in place, because a stored
            //     zero-rated treatment may be a deliberate override (momsfri
            //     verksamhet) that a blind flip would undo on every load.
            //     Instead it is said loudly, with the one-click fix in the
            //     Momshantering section, and the publish guard refuses
            //     (409 VAT_TREATMENT_CONFLICT) as the backstop.
            if (transformedInvoice.status === 'Draft') {
              const storedTreatment = transformedInvoice.vat_treatment || 'taxable';
              const resolvedTreatment = resolveVatTreatment(customerToUse, { vatStatus: tenantVatStatus });
              if (storedTreatment === 'taxable' && resolvedTreatment !== 'taxable') {
                dispatch({
                  type: 'UPDATE_INVOICE_DATA',
                  payload: { vat_treatment: resolvedTreatment }
                });
                toast.info('Momshanteringen har uppdaterats', {
                  description: 'Utkastet var sparat med svensk moms, men kunden ska inte '
                    + 'debiteras det. Se avsnittet Momshantering — spara för att behålla ändringen.'
                });
              } else if (storedTreatment !== 'taxable' && resolvedTreatment === 'taxable') {
                toast.warning('Utkastet saknar moms som kunden ska debiteras', {
                  description: 'Utkastet är sparat utan svensk moms, men kunden ska debiteras moms. '
                    + 'Välj Momspliktig under Momshantering och kontrollera radernas momssats — '
                    + 'annars stoppas publiceringen.',
                  duration: 12000,
                });
              }
            }

            // Then update delivery options separately
            const customerEmail = customerToUse.email ||
              (customerToUse.contacts && customerToUse.contacts.length > 0
                ? customerToUse.contacts[0].contact_email
                : '');

            const customerPhone = customerToUse.phone ||
              (customerToUse.contacts && customerToUse.contacts.length > 0
                ? customerToUse.contacts[0].contact_phone
                : '');



            console.log('Using customer data from API:', {
              customer: customerToUse,
              id: customerToUse.id,
              name: customerToUse.name,
              email: customerEmail,
              phone: customerPhone
            });



            // Update delivery options after customer is set, but only fill empty fields
            dispatch({
              type: 'UPDATE_DELIVERY_OPTIONS',
              payload: {
                email: deliveryOptions.email || customerEmail,
                phone: deliveryOptions.phone || customerPhone,
                method: deliveryOptions.method,
                message: deliveryOptions.message,
                subject: deliveryOptions.subject
              }
            });

          } catch (error) {
            console.log('Using customer data from invoice response');
            dispatch({ type: 'SET_CUSTOMER', payload: customerFromInvoice, preserveDueDate: true });
          }
        }

        // Mark data as loaded after all operations complete
        setTimeout(() => {
          console.log('Final state after loading invoice:', {
            paymentMethods: state.paymentMethods,
            deliveryOptions: state.deliveryOptions,
            selectedTemplate: state.selectedTemplate
          });
          setIsDataLoaded(true);
          setIsInitialized(true);
          setTimeout(() => resetTracking(), 100);
        }, 200); // Longer delay to ensure all state updates complete
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Fel', {
        description: 'Kunde inte hämta fakturadetaljer'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers/${customerId}`),
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/articles`),
        { withCredentials: true }
      );

      setArticles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/sales/customers`),
        { withCredentials: true }
      );
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const saveNewCustomer = async (customerData) => {
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/sales/customers`),
        customerData,
        { withCredentials: true }
      );

      console.log('Customer creation response:', response.data);

      // Refresh customers list first
      await fetchCustomers();

      // Close modal
      setShowNewCustomerModal(false);

      // Show success message
      toast.success('Klart', {
        description: 'Kunden har skapats.'
      });

      // Try to find and auto-select the newly created customer
      // We'll do this after the customers list is refreshed
      setTimeout(async () => {
        try {
          // Get the updated customers list and find the newly created one
          const customersResponse = await axios.get(
            getApiUrl(`${companyId}/sales/customers`),
            { withCredentials: true }
          );
          const updatedCustomers = customersResponse.data || [];

          // Find the customer that matches the created data
          let newCustomer = null;

          if (response.data && response.data.customerId) {
            // API returns customerId in the response
            newCustomer = updatedCustomers.find(c => c.id === response.data.customerId);
            console.log('Searching for customer with ID:', response.data.customerId);
          } else if (response.data && response.data.data && response.data.data.id) {
            // Alternative API structure
            newCustomer = updatedCustomers.find(c => c.id === response.data.data.id);
            console.log('Searching for customer with ID (alt structure):', response.data.data.id);
          } else {
            // Fallback: find by unique identifiers
            console.log('Using fallback matching by customer data');
            newCustomer = updatedCustomers.find(c => {
              if (customerData.type === 'company') {
                return (c.orgnumber && c.orgnumber === customerData.orgnumber) ||
                  (c.name && c.name === customerData.name);
              } else {
                // For individuals, the API might combine first_name + last_name into name field
                const fullName = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim();
                return (c.name && c.name === fullName) ||
                  (c.first_name === customerData.first_name && c.last_name === customerData.last_name);
              }
            });
          }

          if (newCustomer) {
            console.log('Auto-selecting customer:', newCustomer);
            handleFormDataChange({ customer: newCustomer });
          } else {
            console.log('Could not find newly created customer for auto-selection');
          }
        } catch (error) {
          console.error('Error auto-selecting customer:', error);
        }
      }, 500); // Small delay to ensure state updates

      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error('Fel', {
        description: 'Ett fel uppstod när kunden skulle skapas.'
      });
      throw error;
    }
  };

  // Debug effect to track payment methods changes
  useEffect(() => {
    if (isEditMode && state.paymentMethods) {
      console.log('Payment methods changed in state:', state.paymentMethods);
    }
  }, [state.paymentMethods, isEditMode]);

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchArticles(),
        fetchCustomers()
      ]);

      if (isEditMode && invoiceId) {
        await fetchInvoiceDetails();
      } else {
        // For new invoices, check if we have a pre-selected customer (e.g., from customer details page)
        if (preSelectedCustomerId) {
          const customerDetails = await fetchCustomerDetails(preSelectedCustomerId);
          if (customerDetails) {
            dispatch({ type: 'SET_CUSTOMER', payload: customerDetails });

            // D1-5. Derive the treatment here too. Picking a customer in the
            // form does this (handleFormDataChange below), but arriving with
            // ?customerId= — the "Skapa ny faktura" button on a customer —
            // only set the customer, so the invoice kept the initial state's
            // `taxable`. For a bygg-flagged customer the editor then showed
            // "Skattepliktig (ändrad manuellt — automatiskt förslag: Omvänd
            // skattskyldighet (bygg))" on an invoice nobody had touched, and
            // the lines carried 25 % moms until the publish guard refused.
            dispatch({
              type: 'UPDATE_INVOICE_DATA',
              payload: {
                vat_treatment: resolveVatTreatment(customerDetails, { vatStatus: tenantVatStatus }),
              },
            });

            // Auto-populate delivery options with customer contact information
            const customerEmail = customerDetails.email ||
              (customerDetails.contacts && customerDetails.contacts.length > 0
                ? customerDetails.contacts[0].contact_email
                : '');

            const customerPhone = customerDetails.phone ||
              (customerDetails.contacts && customerDetails.contacts.length > 0
                ? customerDetails.contacts[0].contact_phone
                : '');

            if (customerEmail || customerPhone) {
              dispatch({
                type: 'UPDATE_DELIVERY_OPTIONS',
                payload: {
                  recipientEmail: customerEmail,
                  recipientPhone: customerPhone
                }
              });
            }
          }
        }

        // Mark as initialized
        setIsDataLoaded(true);
        setIsInitialized(true);
        setTimeout(() => resetTracking(), 100);
      }
    };

    loadInitialData();
  }, [invoiceId, isEditMode, preSelectedCustomerId]);

  // Form handlers
  const handleFormDataChange = useCallback((updates) => {
    console.log('Form data change:', updates);

    if ('customer' in updates) {
      dispatch({ type: 'SET_CUSTOMER', payload: updates.customer });

      // Auto-derive vat_treatment from customer + tenant vat_status.
      // Read-only on the form; submitted with the invoice on save.
      if (updates.customer) {
        const derivedTreatment = resolveVatTreatment(updates.customer, { vatStatus: tenantVatStatus });
        console.log('Auto-derived vat_treatment:', derivedTreatment, 'from customer.country=', updates.customer.country);
        dispatch({ type: 'UPDATE_INVOICE_DATA', payload: { vat_treatment: derivedTreatment } });
      } else {
        dispatch({ type: 'UPDATE_INVOICE_DATA', payload: { vat_treatment: 'taxable' } });
      }

      // Auto-populate delivery options with customer contact information
      if (updates.customer) {
        const customerEmail = updates.customer.email ||
          (updates.customer.contacts && updates.customer.contacts.length > 0
            ? updates.customer.contacts[0].contact_email
            : '');

        const customerPhone = updates.customer.phone ||
          (updates.customer.contacts && updates.customer.contacts.length > 0
            ? updates.customer.contacts[0].contact_phone
            : '');

        // Use setTimeout to ensure customer is set before updating delivery options
        setTimeout(() => {
          dispatch({
            type: 'UPDATE_DELIVERY_OPTIONS',
            payload: {
              email: customerEmail,
              phone: customerPhone,
              method: state.deliveryOptions?.method || 'download',
              message: state.deliveryOptions?.message || '',
              subject: state.deliveryOptions?.subject || 'Faktura'
            }
          });
        }, 50);

        console.log('Auto-populated delivery options:', {
          email: customerEmail,
          phone: customerPhone
        });
      }
    } else if ('items' in updates) {
      dispatch({ type: 'SET_ITEMS', payload: updates.items });
    } else {
      dispatch({ type: 'UPDATE_INVOICE_DATA', payload: updates });
    }
  }, [tenantVatStatus]);

  // Address and contact selection handlers
  const handleAddressSelect = useCallback((addressId) => {
    dispatch({ type: 'SET_SELECTED_ADDRESS', payload: addressId });
  }, []);

  const handleContactSelect = useCallback((contactId) => {
    dispatch({ type: 'SET_SELECTED_CONTACT', payload: contactId });

    // Update delivery options with the selected contact's email/phone
    if (state.customer?.contacts) {
      const selectedContact = state.customer.contacts.find(c => c.contact_id === contactId);
      if (selectedContact) {
        dispatch({
          type: 'UPDATE_DELIVERY_OPTIONS',
          payload: {
            email: selectedContact.contact_email || state.deliveryOptions.email,
            phone: selectedContact.contact_phone || state.deliveryOptions.phone
          }
        });
      }
    }
  }, [state.customer, state.deliveryOptions]);

  const handleAddressOverride = useCallback((overrideData) => {
    dispatch({ type: 'SET_ADDRESS_OVERRIDE', payload: overrideData });
  }, []);

  const handleContactOverride = useCallback((overrideData) => {
    dispatch({ type: 'SET_CONTACT_OVERRIDE', payload: overrideData });

    // Update delivery options with override contact info
    if (overrideData) {
      dispatch({
        type: 'UPDATE_DELIVERY_OPTIONS',
        payload: {
          email: overrideData.contact_email || state.deliveryOptions.email,
          phone: overrideData.contact_phone || state.deliveryOptions.phone
        }
      });
    }
  }, [state.deliveryOptions]);

  const handleResetAddressOverride = useCallback(() => {
    dispatch({ type: 'RESET_ADDRESS_OVERRIDE' });
  }, []);

  const handleResetContactOverride = useCallback(() => {
    dispatch({ type: 'RESET_CONTACT_OVERRIDE' });

    // Reset delivery options to the selected contact's info
    if (state.customer?.contacts && state.selectedContactId) {
      const selectedContact = state.customer.contacts.find(c => c.contact_id === state.selectedContactId);
      if (selectedContact) {
        dispatch({
          type: 'UPDATE_DELIVERY_OPTIONS',
          payload: {
            email: selectedContact.contact_email || '',
            phone: selectedContact.contact_phone || ''
          }
        });
      }
    } else if (state.customer?.contacts?.[0]) {
      // Fall back to first contact
      dispatch({
        type: 'UPDATE_DELIVERY_OPTIONS',
        payload: {
          email: state.customer.contacts[0].contact_email || state.customer.email || '',
          phone: state.customer.contacts[0].contact_phone || state.customer.phone || ''
        }
      });
    }
  }, [state.customer, state.selectedContactId]);

  // Update the handleSaveDraft function:
  const handleSaveDraft = useCallback(async () => {
    if (!state.customerid) {
      toast.error('Fel', {
        description: 'Vänligen välj en kund'
      });
      return null;
    }

    if (state.items.length === 0) {
      toast.error('Fel', {
        description: 'Lägg till minst en artikel'
      });
      return null;
    }

    setLoading(true);
    try {
      const dataToSend = {
        customer_id: state.customerid,
        invoice_number: state.invoiceNumber,
        invoice_date: formatISO(state.invoiceDate, { representation: 'date' }),
        due_date: formatISO(state.dueDate, { representation: 'date' }),
        currency: state.currency,
        round_total: state.roundTotal,
        notes: state.notes,
        status: 'Draft',
        selected_template: state.selectedTemplate,
        vat_treatment: state.vat_treatment || 'taxable',
        items: state.items.map(item => ({
          article_id: item.article_id,
          product_code: item.productcode,
          description: item.description,
          unit_price: item.unitprice,
          quantity: item.quantity,
          unit: item.unit,
          tax_rate: item.tax_rate,
          discount: item.discount || 0,
          type: item.type || 'Product',
          item_id: item.id && !item.isNew ? item.id : undefined
        })),
        delivery_options: state.deliveryOptions,
        payment_methods: Object.keys(state.paymentMethods).filter(m => state.paymentMethods[m]),
        address_override: state.invoiceAddressOverride || null,
        contact_override: state.invoiceContactOverride || null,
        use_accrual_method: state.use_accrual_method || false
      };

      console.log('Data to send for saving:', dataToSend);
      console.log('Current state before save:', state);

      let response;

      if (isEditMode) {
        response = await axios.put(
          getApiUrl(`${companyId}/invoices/${invoiceId}`),
          dataToSend,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          getApiUrl(`${companyId}/invoices`),
          dataToSend,
          { withCredentials: true }
        );
      }

      if (response.data.success) {
        resetTracking();
        skipNavigationWarningRef.current = true;

        toast.success('Klart', {
          description: isEditMode ? 'Fakturan uppdaterad' : 'Fakturan sparad'
        });

        const savedInvoiceId = response.data.data?.invoice_id || response.data.invoiceId || invoiceId;

        // If creating new invoice, redirect to edit mode
        if (!isEditMode && savedInvoiceId) {
          history.replace(`/${companyId}/edit-invoice/${savedInvoiceId}`);
        }

        return savedInvoiceId;
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Fel', {
        description: error.response?.data?.message || 'Kunde inte spara fakturan'
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [state, isEditMode, companyId, invoiceId, resetTracking, history]);

  const [periodisationReview,setPeriodisationReview]=useState(null);
  const handlePublish = useCallback(async () => {
    const hasSelectedPaymentMethod = Object.values(state.paymentMethods).some(v => v);
    if (!hasSelectedPaymentMethod) {
      toast.warning('Varning', {
        description: 'Vänligen välj minst en betalningsmetod'
      });
      return;
    }

    if (!isFormValid) {
      toast.warning('Varning', {
        description: 'Vänligen åtgärda valideringsfel'
      });
      return;
    }

    let invoiceIdToPublish = invoiceId;

    // Save changes before publishing if needed
    if (hasChanges || !isEditMode) {
      const confirmSave = await swal({
        title: hasChanges ? 'Osparade ändringar' : 'Spara och publicera',
        text: hasChanges
          ? 'Vill du spara utkastet innan du publicerar fakturan?'
          : 'Fakturan måste sparas innan den kan publiceras. Fortsätt?',
        icon: 'warning',
        buttons: ['Avbryt', 'Spara och publicera'],
      });

      if (confirmSave) {
        invoiceIdToPublish = await handleSaveDraft();
        if (!invoiceIdToPublish) return;
      } else {
        return;
      }
    }

    try {
      setLoading(true);

      console.log('Publishing invoice with ID:', invoiceIdToPublish, state.deliveryOptions);


      // One call site, so the moms-conflict retry below reuses the success path
      // rather than duplicating it.
      // Publishing books the invoice and gives it its number. Delivery is a
      // separate step the user takes from the list or the invoice page:
      // e-mail or PDF download, whenever they choose.
      const periodisation=await new Promise(resolve=>setPeriodisationReview({cash:accountingSettings?.accountingMethod==='cash' && !state.use_accrual_method,invoiceId:invoiceIdToPublish,date:formatISO(state.invoiceDate,{representation:'date'}),resolve}));
      if(!periodisation) return;
      const runPublish = (confirmVatConflict) => axios.post(
        getApiUrl(`${companyId}/invoices/${invoiceIdToPublish}/publish`),
        {
          action: 'storeOnly',
          ...periodisation,
          paymentMethods: Object.keys(state.paymentMethods).filter(m => state.paymentMethods[m]),
          ...(confirmVatConflict ? { confirmVatConflict: true } : {}),
        },
        { withCredentials: true }
      );

      try {
        await runPublish(false);
      } catch (publishError) {
        const conflict = await readVatConflict(publishError);
        if (!conflict) throw publishError;

        // The server refuses to publish an invoice whose moms disagrees with its
        // customer in either direction (batch H, generalized in C1). Sending it
        // anyway is a deliberate choice, so it is a confirm rather than a toast
        // the user can miss.
        const proceed = await swal({
          title: 'Momsen stämmer inte med kunden',
          text: conflict.message,
          icon: 'warning',
          buttons: {
            cancel: { text: 'Gå tillbaka och rätta', value: null, visible: true },
            confirm: { text: 'Skicka ändå', value: true, visible: true },
          },
          dangerMode: true,
        });
        if (!proceed) {
          setLoading(false);
          return;
        }
        await runPublish(true);
      }

      resetTracking();
      skipNavigationWarningRef.current = true;
      toast.success('Fakturan är publicerad', {
        description: 'Skicka den med e-post eller ladda ner PDF:en från fakturalistan.',
        duration: 8000,
      });
      history.push(`/${companyId}/invoices`, { publishedInvoiceId: invoiceIdToPublish });
    } catch (error) {
      let msg = 'Fakturan kunde inte publiceras.';
      let parsedData = null;
      try {
        const data = error.response?.data;
        if (data instanceof Blob) {
          parsedData = JSON.parse(await data.text());
        } else if (data) {
          parsedData = data;
        }
        if (parsedData) msg = parsedData.error || parsedData.message || msg;
      } catch (e) { /* fallback to default */ }

      toast.error('Fel', {
        description: msg
      });
    } finally {
      setLoading(false);
    }
  }, [state, hasChanges, isFormValid, isEditMode, invoiceId, companyId, handleSaveDraft, resetTracking, history, accountingSettings]);

  // Add this function with the other handlers
  const handleDeleteDraft = useCallback(async () => {
    // A silent return is what made this button look broken: clicking it on
    // anything but a draft produced no dialog, no toast and no navigation —
    // indistinguishable from a dead control.
    if (!isEditMode) {
      toast.warning('Varning', {
        description: 'Fakturan är inte sparad än, så det finns inget utkast att radera.'
      });
      return;
    }
    if (state.status !== 'Draft') {
      toast.warning('Varning', {
        description: `Fakturan har status ${state.status} och kan inte raderas. `
          + 'Skapa en kreditfaktura om den behöver annulleras.'
      });
      return;
    }

    // The dialog itself is wrapped: if it ever fails to open, the click must not
    // disappear. A confirm that throws left this handler with nothing rendered
    // and nothing logged, which is indistinguishable from a dead button — the
    // symptom reported from the browser pass, and one I could not reproduce from
    // the code alone.
    let confirmed;
    try {
      confirmed = await swal({
        title: 'Är du säker?',
        text: 'Vill du radera detta fakturautkast? Det går inte att ångra.',
        icon: 'warning',
        buttons: ['Avbryt', 'Radera utkast'],
        dangerMode: true,
      });
    } catch (dialogError) {
      console.error('Delete confirmation dialog failed:', dialogError);
      toast.error('Fel', {
        description: 'Bekräftelsedialogen kunde inte visas. Ladda om sidan och försök igen.'
      });
      return;
    }

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(
        getApiUrl(`${companyId}/invoices/${invoiceId}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        resetTracking();
        skipNavigationWarningRef.current = true;
        toast.success('Klart', { description: 'Fakturautkastet är raderat.' });
        history.push(`/${companyId}/invoices`);
      } else {
        throw new Error(response.data.message || 'Något gick fel');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Fel', {
        description: error.response?.data?.message
          || 'Kunde inte radera fakturautkastet. Försök igen senare.'
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, invoiceId, isEditMode, state.status, history, resetTracking]);

  // Article management
  const handleSaveArticle = async (newArticle) => {
    try {
      const response = await axios.post(
        getApiUrl(`${companyId}/articles`),
        newArticle,
        { withCredentials: true }
      );
      if (response.data.success) {
        await fetchArticles();
        toast.success('Klart', {
          description: 'Artikeln sparades'
        });
      }
    } catch (error) {
      toast.error('Fel', {
        description: 'Kunde inte spara artikeln'
      });
    }
    setShowNewArticleModal(false);
  };

  // Update the navigation warning effect:
  useEffect(() => {
    const unblock = history.block((location) => {
      // Skip warning for programmatic navigation (publish, save, delete)
      if (skipNavigationWarningRef.current) {
        skipNavigationWarningRef.current = false;
        return true;
      }

      if (hasChanges && isInitialized) {
        // If we're navigating to the edit version of the same invoice, allow it
        const isNavigatingToEditSameInvoice = location.pathname.includes(`/edit-invoice/`) &&
          !isEditMode;

        if (isNavigatingToEditSameInvoice) {
          return true;
        }

        swal({
          title: 'Du har osparade ändringar!',
          text: 'Vill du spara utkastet innan du lämnar sidan?',
          icon: 'warning',
          buttons: {
            cancel: 'Avbryt',
            discard: {
              text: 'Fortsätt utan att spara',
              value: 'discard',
            },
            save: {
              text: 'Spara utkast',
              value: 'save',
            },
          },
        }).then((value) => {
          switch (value) {
            case 'save':
              handleSaveDraft().then((savedId) => {
                if (savedId) {
                  skipNavigationWarningRef.current = true;
                  history.push(location.pathname);
                }
              });
              break;
            case 'discard':
              skipNavigationWarningRef.current = true;
              history.push(location.pathname);
              break;
            default:
              break;
          }
        });

        return false;
      }
      return true;
    });

    return () => unblock();
  }, [hasChanges, isInitialized, history, handleSaveDraft, isEditMode]);

  // Browser-level refresh / tab-close warning. history.block above only
  // catches in-app navigation; refresh and close fire `beforeunload` and
  // need a separate listener. Always-on, reads dirty state via a ref so
  // we don't race with effect ordering. Modern browsers require a
  // non-empty returnValue before they'll show the native prompt.
  const isDirtyRef = useRef(false);
  useEffect(() => {
    isDirtyRef.current = hasChanges && isInitialized;
  }, [hasChanges, isInitialized]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirtyRef.current) return undefined;
      event.preventDefault();
      event.returnValue = 'Du har osparade ändringar.';
      return 'Du har osparade ändringar.';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Only an existing invoice has a status worth showing; a new one is a draft
  // by definition.
  const statusBadge = isEditMode && state.status && (
    <span className={`inv-badge ${state.status === 'Draft' ? 'inv-badge--warn' : ''} ms-2 align-middle`}>
      {state.status === 'Draft' ? 'Utkast' : 'Publicerad'}
    </span>
  );

  // Cash-method tenants can periodisera a single invoice. Rendered with the
  // other switches at the bottom of Fakturadetaljer.
  const accrualToggle = accountingSettings?.accountingMethod === 'cash' ? (
    <div className="inv-toggle-row">
      <div>
        <div className="inv-toggle-row__text">Periodisera faktura</div>
        <div className="inv-help">
          Lägger till ett bokföringssteg efter publicering – krävs om betalningen sker i nästa räkenskapsår
        </div>
      </div>
      <Form.Check
        type="switch"
        id="inv-accrual-switch"
        aria-label="Periodisera faktura"
        checked={Boolean(state.use_accrual_method)}
        onChange={(e) => dispatch({ type: 'UPDATE_INVOICE_DATA', payload: { use_accrual_method: e.target.checked } })}
      />
    </div>
  ) : null;

  // Momshantering: what was decided, why, and what was assumed. The
  // assumption is the whole point — a customer with no country stored reads
  // as Swedish, and one such invoice went to a German business with 25 %
  // moms. The server enforces the treatment; this is the user's view of it.
  // Rendered as a field in Fakturadetaljer with its notes underneath.
  const vat = state.customer ? (() => {
    const explained = explainVatTreatment(state.customer, { vatStatus: tenantVatStatus });
    const overridden = state.vat_treatment && state.vat_treatment !== explained.treatment;
    const shown = state.vat_treatment || explained.treatment;
    const shownDescription = describeVatTreatment(shown, { vatStatus: tenantVatStatus });
    return { explained, overridden, shown, shownDescription };
  })() : null;

  const vatField = vat && (
    <div>
      <label className="inv-label" htmlFor="inv-vat-treatment">Moms</label>
      <select
        id="inv-vat-treatment"
        className="inv-control"
        value={vat.shown}
        onChange={(e) => dispatch({ type: 'UPDATE_INVOICE_DATA', payload: { vat_treatment: e.target.value } })}
      >
        {VAT_TREATMENTS.map((t) => (
          <option key={t} value={t}>{VAT_TREATMENT_LABELS[t]}</option>
        ))}
      </select>
    </div>
  );

  const vatNotes = vat && (
    <div className="inv-field-notes">
      <div className="inv-help">
        {vat.shownDescription.reason}
        {vat.shownDescription.zeroRated && ' Momsen sätts till 0 kr på raderna.'}
        {vat.shownDescription.zeroRated && vat.shownDescription.documentNote && (
          <> Texten ”{vat.shownDescription.documentNote}” och kundens momsnummer skrivs på fakturan.</>
        )}
        {vat.overridden && (
          <> Ändrad manuellt – automatiskt förslag: {VAT_TREATMENT_LABELS[vat.explained.treatment]}.</>
        )}
      </div>
      {vat.explained.assumption && (
        <div className="inv-note mt-2" role="note">{vat.explained.assumption}</div>
      )}
    </div>
  );

  return (
    <>
      <div className="inv-form">
        <PageHeader
          icon={Receipt}
          title={<>
            {isEditMode
              ? (state.invoiceNumber ? `Faktura ${state.invoiceNumber}` : 'Redigera faktura')
              : 'Ny faktura'}
            {statusBadge}
          </>}
          subtitle={state.customer?.name || undefined}
          backTo={{ to: `/${companyId}/invoices`, label: 'Kundfakturor' }}
          feedbackSource="skapa-faktura"
        />

        {/* ML 18 kap (omsättningsgräns) or ML 10 kap (aktivitetsundantag): the
            server forces vat_treatment='exempt' regardless of customer flags;
            this line is user information. */}
        {(tenantVatStatus === 'exempt' || tenantVatStatus === 'not_registered') && (
          <div className="inv-note mb-3">
            <strong>Företaget är momsbefriat.</strong>{' '}
            {tenantVatStatus === 'not_registered'
              ? 'Säljaren är inte momsregistrerad enligt mervärdesskattelagen 18 kap (under omsättningsgränsen). Ingen moms läggs på fakturan.'
              : 'Säljaren har aktivitetsundantag enligt mervärdesskattelagen 10 kap. Ingen moms läggs på fakturan; undantagsgrunden visas på fakturan.'}
          </div>
        )}

        <CustomerCompactCard
          customers={customers}
          selectedCustomer={state.customer}
          onCustomerSelect={(customer) => handleFormDataChange({ customer })}
          onFetchCustomerDetails={fetchCustomerDetails}
          onCreateCustomer={() => setShowNewCustomerModal(true)}
          selectedAddressId={state.selectedAddressId}
          selectedContactId={state.selectedContactId}
          addressOverride={state.invoiceAddressOverride}
          contactOverride={state.invoiceContactOverride}
          onAddressSelect={handleAddressSelect}
          onContactSelect={handleContactSelect}
          onAddressOverride={handleAddressOverride}
          onContactOverride={handleContactOverride}
          onResetAddressOverride={handleResetAddressOverride}
          onResetContactOverride={handleResetContactOverride}
        />

        {state.customer && (
          <>
            <InvoiceFormBootstrap
              formData={{
                customerid: state.customerid,
                customer: state.customer,
                invoiceNumber: state.invoiceNumber,
                invoiceDate: state.invoiceDate,
                dueDate: state.dueDate,
                currency: state.currency,
                roundTotal: state.roundTotal,
                items: state.items,
                notes: state.notes,
                defaultVatRate: state.defaultVatRate,
                vat_treatment: state.vat_treatment
              }}
              onFormDataChange={handleFormDataChange}
              availableArticles={articles}
              invoiceSettings={invoiceSettings}
              validationErrors={validationErrors}
              vatField={vatField}
              vatNotes={vatNotes}
              extraSwitches={accrualToggle}
            />

            {paymentProblem && (
              <div className="inv-note mb-3">
                {paymentProblem}{' '}
                <Link to={`/${companyId}/settings/invoice#betalningsmetoder`} target="_blank" rel="noopener noreferrer">
                  Öppna inställningar
                </Link>
              </div>
            )}

          </>
        )}

        {periodisationReview && <InvoicePeriodisationReview companyId={companyId} review={periodisationReview}
          onClose={value=>{periodisationReview.resolve(value);setPeriodisationReview(null);}}/>}
        <InvoiceStickyBar
          total={invoiceTotals.finalTotal}
          currency={state.currency}
          isTaxable={isTaxableInvoice}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onDelete={handleDeleteDraft}
          showDeleteButton={isEditMode && state.status === 'Draft'}
          loading={loading}
          hasUnsavedChanges={hasChanges && isInitialized}
          publishBlockedReason={publishBlockedReason}
        />

        <NewArticleModal
          show={showNewArticleModal}
          handleClose={() => setShowNewArticleModal(false)}
          handleSave={handleSaveArticle}
          articleDetails={selectedArticle}
        />

        <NewCustomerModal
          show={showNewCustomerModal}
          handleClose={() => setShowNewCustomerModal(false)}
          handleSave={saveNewCustomer}
        />
      </div>
    </>
  );
};

export default InvoiceForm;
